import { ArcgisMapCustomEvent } from "@arcgis/map-components";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Point from "@arcgis/core/geometry/Point";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import TileInfo from "@arcgis/core/layers/support/TileInfo.js";
import _ from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppConfig } from "../../AppConfig";
import Basemap from "@arcgis/core/Basemap";
import Graphic from "@arcgis/core/Graphic";
import * as intl from "@arcgis/core/intl.js";
import WebMap from "@arcgis/core/WebMap";
import { AppContext, FeatureTableContext, LighthouseContext, OverettContext } from "../../Context";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import { useDispatch, useSelector } from "react-redux";
import {
    addDefaultMinScale,
    setCreateObjectOpen,
    setHasUnsavedChanges,
    setIsOpeningTool,
    setLeftPanelActiveTab,
    setLightCalculatorOpen,
    setMapScale,
    setMeasureToolOpen,
    setViewLoaded,
} from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import Ground from "@arcgis/core/Ground.js";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer.js";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Viewpoint from "@arcgis/core/Viewpoint.js";
import { FeatureLayerNFS, ImageryLayerNFS } from "../../extensions/FeatureLayerNFS";
import { setIsEditing } from "../../store/lighthouseSlice";
import { getViewPoint, storeViewPoint } from "../../utils/localStorageUtils";
import Loader from "../genericComponents/Loader/Loader";
import {
    lighthouseHandlerAddSector,
    lighthouseHandlerClickSector,
    lighthouseHandlerDrag,
    lighthouseHandlerPointerEditingLayer,
    lighthouseHandlerPointerMove,
    lighthouseOverlayHandler,
    sectorLineClickHandler,
} from "../MapTools/LightHouse/LightHouseControl";
import Scale from "../Scale/Scale";
import "./MapControl.css";
import HighlightOptions from "@arcgis/core/views/support/HighlightOptions.js";
import "@arcgis/map-components/components/arcgis-coordinate-conversion";
import "@arcgis/map-components/components/arcgis-expand";
import "@arcgis/map-components/components/arcgis-layer-list";
import "@arcgis/map-components/components/arcgis-legend";
import "@arcgis/map-components/components/arcgis-locate";
import "@arcgis/map-components/components/arcgis-map";
import "@arcgis/map-components/components/arcgis-measurement";
import "@arcgis/map-components/components/arcgis-scale-bar";
import "@arcgis/map-components/components/arcgis-zoom";
import { LeftPanelTabs } from "../../constants/Panels";
import { setOverettAngle } from "../../store/overettSlice";
import { ItemDisplayType, setClickResultsLoading, setSearchItemDisplayType } from "../../store/searchSlice";
import { triggerLayerlistRefresh } from "../../store/triggersSlice";
import {
    getMainRelatedFeature,
    indicateDiscontinuedOrRemovedFeatures,
    removedAndDiscontinuedDefExpr,
    setupFlytendemerkeTildeltCircles,
    Tool,
} from "../../utils/arcgisUtils";
import { isNumber, layerHasField } from "../../utils/helpers";
import { addJobsTablesToMap } from "../../utils/jobsUtils";
import { LighthouseData } from "../../utils/lighthouseConstants";
import { queryHighResDepth, queryKYVHighResDepth } from "../../utils/seacableUtils";
import { loadExtentFromUrl } from "../../utils/urlUtils";
import { overettHandlerDrag, overettHandlerPointerMove } from "../MapTools/Overett/OverettControl";
import MapOverlay from "./MapOverlay";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import { FeatureClasses } from "../../constants/NauticalObjects";
import { LayerFieldsIB } from "../../constants/LayerFields";
import { JaNei } from "../../constants/nfsDomains";
import type View2DConstraints from "@arcgis/core/views/2d/MapViewConstraints";
import type { SceneViewGraphicHit } from "@arcgis/core/views/types";
import type Layer from "@arcgis/core/layers/Layer";
import type { LayerViewCreateEvent } from "@arcgis/core/views/input/types";
import type FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import type MapView from "@arcgis/core/views/MapView";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

const MapControl = () => {
    const dispatch = useDispatch();

    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);
    const overettContext = useContext(OverettContext);
    const featureTableContext = useContext(FeatureTableContext);

    const overettLinjeRef = overettContext?.overettLinjeRef.value;
    const selectedLineRef = lighthouseContext?.selectedLineRef.value;

    const esriLayerListOpen = useSelector((state: StoreState) => state.app.esriLayerListOpen);
    const trueNorth = useSelector((state: StoreState) => state.lighthouse.currentLighthouseTrueNorth);
    const loading = useSelector((state: StoreState) => state.app.loading);
    const showSectorColors = useSelector((state: StoreState) => state.lighthouse.showSectorColors);
    const sectorOpacity = useSelector((state: StoreState) => state.lighthouse.sectorOpacity);
    const isSectorEditorOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditorOpen);
    const isSectorEditTableOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditTableOpen);
    const isDraggingOverettLinje = useSelector((state: StoreState) => state.overett.isDraggingOverettLinje);
    const lighthouseTableSelectedSector = useSelector(
        (state: StoreState) => state.lighthouse.lighthouseTableSelectedSector
    );
    const showRemovedFeatures = useSelector((state: any) => state.app.showRemovedFeatures);
    const isOpeningTool = useSelector((state: StoreState) => state.app.isOpeningTool);

    const showSectorColorsRef = useRef(showSectorColors);
    const sectorOpacityRef = useRef(sectorOpacity);
    const arcgisMapRef = useRef<HTMLArcgisMapElement | null>(null);
    const esriHandleRef = useRef<ResourceHandle[]>([]);
    const currentLine = useRef<Graphic | null>(null);
    const temporaryLine = useRef<Graphic | null>(null);
    const isDraggingLine = useRef(false);
    const trueNorthRef = useRef(0);
    const mouseHoverHighlightRef = useRef<Graphic | null>(null);
    const isSectorEditorOpenRef = useRef<boolean>(isSectorEditorOpen);
    const isSectorEditTableOpenRef = useRef<boolean>(isSectorEditTableOpen);
    const isDraggingOverettLinjeRef = useRef(isDraggingOverettLinje);
    const lighthouseData = lighthouseContext!.lighthouseData.value;
    const lighthouseDataRef = useRef<LighthouseData>(lighthouseContext!.lighthouseData.value);
    const lighthouseTableSelectedSectorRef = useRef(lighthouseTableSelectedSector);
    const selectedFeatureRef = useRef<Graphic | null>(appContext?.selectedFeature.value ?? null);
    const dragHandleHover = useRef(false);

    const debounceViewpointSave = _.debounce((viewpoint: Viewpoint) => {
        storeViewPoint(viewpoint);
    }, 200);

    const onLayerViewCreate = (event: LayerViewCreateEvent, view: MapView) => {
        reactiveUtils.watch(
            () => event.layerView.visible,
            () => {
                dispatch(triggerLayerlistRefresh());
            }
        );

        if (event.layer instanceof FeatureLayer && event.layer.layerId === FeatureClasses.FlytendemerkeTildelt) {
            const layerView = event.layerView as FeatureLayerView;
            const graphicsLayer = new GraphicsLayer({
                id: "FlytendemerkeTildelt_circles",
                listMode: "hide",
                minScale: 1000,
            });

            view.map?.add(graphicsLayer, 0);
            event.layer.renderer = null; // Hide the original point layer; do not turn off visibility as we still need it for querying
            setupFlytendemerkeTildeltCircles(layerView, view, graphicsLayer);
        }
    };

    // Use hooks to update the ref values when the context values change
    useEffect(() => {
        lighthouseDataRef.current = lighthouseData;
    }, [lighthouseData]);
    useEffect(() => {
        showSectorColorsRef.current = showSectorColors;
    }, [showSectorColors]);
    useEffect(() => {
        sectorOpacityRef.current = sectorOpacity;
    }, [sectorOpacity]);
    useEffect(() => {
        trueNorthRef.current = trueNorth;
    }, [trueNorth]);
    useEffect(() => {
        // The sector table holds a draft of the same sectors, so while it is open the map only
        // pans and zooms: editing them here as well would be undone by the table's save
        isSectorEditorOpenRef.current = isSectorEditorOpen && !isSectorEditTableOpen;
        isSectorEditTableOpenRef.current = isSectorEditTableOpen;
        // A line hovered just before the table opened would otherwise still drag
        if (isSectorEditTableOpen) currentLine.current = null;
    }, [isSectorEditorOpen, isSectorEditTableOpen]);
    useEffect(() => {
        isDraggingOverettLinjeRef.current = isDraggingOverettLinje;
    }, [isDraggingOverettLinje]);
    useEffect(() => {
        lighthouseTableSelectedSectorRef.current = lighthouseTableSelectedSector;
    }, [lighthouseTableSelectedSector]);
    useEffect(() => {
        selectedFeatureRef.current = appContext?.selectedFeature.value ?? null;
    }, [appContext?.selectedFeature.value]);

    useEffect(() => {
        if (isOpeningTool === null) return;
        if (isOpeningTool !== Tool.MEASURE) dispatch(setMeasureToolOpen(false));
        if (isOpeningTool !== Tool.LIGHT_CALCULATOR) {
            dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
        }
        if (isOpeningTool !== Tool.CREATE_OBJECT) dispatch(setCreateObjectOpen(false));

        dispatch(setIsOpeningTool(null));
    }, [isOpeningTool]);

    const compareFeatures = (a: Graphic, b: Graphic) => a.attributes.objectid === b.attributes.objectid;

    const addAuxiliaryLayer = (map: WebMap, layerId: string) => {
        let layer = map.findLayerById(layerId) as GraphicsLayer;
        if (!layer) {
            layer = new GraphicsLayer({ id: layerId, listMode: "hide" });
            map.add(layer);
        }
    };

    const queryHighResDepthRef = useRef(queryHighResDepth);

    // Update the ref whenever the function changes
    useEffect(() => {
        queryHighResDepthRef.current = queryHighResDepth;
    }, [queryHighResDepth]);

    const queryKYVHighResDepthRef = useRef(queryKYVHighResDepth);

    // Update the ref whenever the function changes
    useEffect(() => {
        queryKYVHighResDepthRef.current = queryKYVHighResDepth;
    }, [queryKYVHighResDepth]);

    const handleViewReady = (event: ArcgisMapCustomEvent<void>) => {
        arcgisMapRef.current = event.target;

        const view = event.target.view;
        const lods = TileInfo.create({
            spatialReference: view.spatialReference,
            numLODs: 26,
        }).lods;
        view.constraints.lods = lods;
        view.popupEnabled = true;
        view.popup = { defaultPopupTemplateEnabled: true };

        const overettHighlightOptions = new HighlightOptions({
            name: "overett",
            color: "magenta",
        });
        view.highlights.push(overettHighlightOptions);

        const layerviewCreateHandle = view.on("layerview-create", (event: LayerViewCreateEvent) => {
            onLayerViewCreate(event, view);
        });
        esriHandleRef.current.push(layerviewCreateHandle);

        const extent = loadExtentFromUrl();

        let viewpoint = getViewPoint();
        if (!viewpoint) {
            viewpoint = new Viewpoint({
                scale: AppConfig.Scale.DefaultScale,
                targetGeometry: new Point({
                    x: -30953.47490385635,
                    y: 6573400.990637887,
                    spatialReference: view.spatialReference,
                }),
            });
            storeViewPoint(viewpoint);
        }
        if (viewpoint.targetGeometry?.spatialReference?.wkid !== view.spatialReference?.wkid) {
            if (viewpoint.targetGeometry) {
                viewpoint.targetGeometry.spatialReference = view.spatialReference;
            }
        }

        if (extent?.x && isNumber(extent.x)) {
            (viewpoint.targetGeometry as Point).x = Number.parseInt(extent.x);
        }

        if (extent?.y && isNumber(extent.y)) {
            (viewpoint.targetGeometry as Point).y = Number.parseInt(extent.y);
        }

        if (extent?.scale && isNumber(extent.scale)) {
            viewpoint.scale = Number.parseInt(extent.scale);
        }

        dispatch(setMapScale(viewpoint.scale));
        view.viewpoint = viewpoint;

        esriHandleRef.current.push(
            view.on("click", async (event) => {
                dispatch(setSearchItemDisplayType(ItemDisplayType.Click));
                dispatch(setClickResultsLoading(true));
                appContext?.clickResults.set([]); // Clear previous results
                const layerIds = AppConfig.Layers.map((layer) => layer.Name);
                const layers = layerIds
                    .map((layerId) => view.map?.findLayerById(layerId))
                    .filter((layer): layer is Layer => layer != null);

                const response = await view.hitTest(event, { include: layers });
                const hitResults = response.results.filter((result) => {
                    const graphic = (result as SceneViewGraphicHit).graphic;
                    // Skip IB features linked to FL/LA, they are edited through their main feature
                    return !(
                        graphic?.layer?.id === "IB" && graphic.attributes?.[LayerFieldsIB.tilknyttet_fl_la] === JaNei.Ja
                    );
                });
                if (hitResults.length > 0) {
                    const features: Graphic[] = [];

                    const foundFeatures = hitResults.map(async (result) => {
                        const feature = await getMainRelatedFeature((result as SceneViewGraphicHit).graphic);
                        if (feature) {
                            features.push(feature);
                        }
                        appContext?.clickResults.set(_.uniqWith(features, compareFeatures));
                    });
                    await Promise.all(foundFeatures);
                    dispatch(setLeftPanelActiveTab(LeftPanelTabs.SEARCH));
                    dispatch(setClickResultsLoading(false));
                } else {
                    dispatch(setClickResultsLoading(false));
                }
            })
        );

        esriHandleRef.current.push(
            reactiveUtils.when(
                () => view.scale,
                (value) => {
                    if (value < AppConfig.Scale.MaxScale) {
                        view.scale = AppConfig.Scale.MaxScale;
                    } else if (value > AppConfig.Scale.MinScale) {
                        view.scale = AppConfig.Scale.MinScale;
                    }
                    dispatch(setMapScale(value));
                }
            )
        );

        esriHandleRef.current.push(
            reactiveUtils.when(
                () => view.stationary,
                () => {
                    const url = new URL(window.location.href);
                    const searchParams = new URLSearchParams(url.searchParams);
                    searchParams.set("x", view.center.x.toFixed(0));
                    searchParams.set("y", view.center.y.toFixed(0));
                    searchParams.set("scale", view.scale.toFixed(0));
                    window.history.pushState({}, "", `${window.location.origin}?${searchParams.toString()}`);

                    // Use the ref to ensure we always call the latest version of the function
                    queryHighResDepthRef.current(view.extent);

                    if (AppConfig.KyvDepthFeatureServerUrl !== "") queryKYVHighResDepthRef.current(view.extent);
                }
            )
        );

        esriHandleRef.current.push(
            reactiveUtils.on(
                () => view.popup?.viewModel,
                "trigger-action",
                (event) => {
                    // If the zoom-out action is clicked, fire the zoomOut() function
                    if (event.action.id === "open") {
                        appContext?.selectedFeature.set(view.popup?.selectedFeature ?? null);
                        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
                    }
                }
            )
        );

        esriHandleRef.current.push(
            reactiveUtils.watch(
                () => view.viewpoint,
                (newValue: Viewpoint) => {
                    debounceViewpointSave(newValue);
                }
            )
        );

        esriHandleRef.current.push(sectorLineClickHandler(view, currentLine, selectedLineRef!));

        esriHandleRef.current.push(
            lighthouseHandlerPointerMove({
                view,
                currentLine,
                isDraggingLine,
                isSectorEditorOpenRef,
                lighthouseData: lighthouseDataRef,
                mouseHoverHighlightRef,
                selectedLine: selectedLineRef!,
                selectedFeatureRef,
            })
        );
        esriHandleRef.current.push(
            lighthouseOverlayHandler(
                view,
                isSectorEditorOpenRef,
                dragHandleHover,
                isDraggingLine,
                currentLine,
                selectedLineRef!
            )
        );
        esriHandleRef.current.push(
            lighthouseHandlerDrag({
                view,
                currentLine,
                selectedLine: selectedLineRef!,
                temporaryLine,
                dragHandleHover,
                lighthouseData: lighthouseDataRef,
                setLighthouseData: (value) => {
                    lighthouseContext?.lighthouseData.set(value);
                    dispatch(setHasUnsavedChanges(true));
                },
                isDraggingLine,
                setIsEditing: (value) => dispatch(setIsEditing(value)),
                trueNorth: trueNorthRef,
                sectorOpacity: sectorOpacityRef,
                showSectorColors: showSectorColorsRef,
                lighthouseTableSelectedSector: lighthouseTableSelectedSectorRef,
                mouseHoverHighlightRef,
            })
        );
        esriHandleRef.current.push(lighthouseHandlerPointerEditingLayer(view, isDraggingLine, isSectorEditorOpenRef));

        esriHandleRef.current.push(
            lighthouseHandlerAddSector(
                view,
                lighthouseDataRef,
                (value) => {
                    lighthouseContext?.lighthouseData.set(value);
                    dispatch(setHasUnsavedChanges(true));
                },
                isSectorEditTableOpenRef
            )
        );

        esriHandleRef.current.push(
            lighthouseHandlerClickSector(
                view,
                lighthouseContext?.lighthouseSectorPanelsOpen.set,
                isSectorEditTableOpenRef
            )
        );

        esriHandleRef.current.push(overettHandlerPointerMove(view, overettLinjeRef));
        esriHandleRef.current.push(
            overettHandlerDrag(
                view,
                (value) => (overettLinjeRef!.current = value),
                isDraggingOverettLinjeRef,
                (angle: number) => dispatch(setOverettAngle(angle)),
                overettContext?.overettObserver.set!
            )
        );

        featureTableContext?.activeLayer.set(view.map?.findLayerById("Lys") as FeatureLayer);

        dispatch(setViewLoaded());

        indicateDiscontinuedOrRemovedFeatures(view);
    };

    const [webMap, setWebMap] = useState<WebMap>();

    useEffect(() => {
        if (esriLayerListOpen) {
            document.getElementsByClassName("esri-layer-list")[0]?.classList.add("layer-list-visible");
        } else {
            document.getElementsByClassName("esri-layer-list")[0]?.classList.remove("layer-list-visible");
        }
    }, [esriLayerListOpen]);

    useEffect(() => {
        intl.setLocale("nb");

        const vectorMap = new Basemap({
            baseLayers: [
                new VectorTileLayer({
                    url: AppConfig.Basemaps.find((basemap) => basemap.Name === "Default")?.ServiceUrl,
                }),
            ],
        });

        const heightMap = new Ground({
            layers: [
                new ElevationLayer({
                    url: AppConfig.HeightmapUrl,
                }),
            ],
        });

        const map = new WebMap({
            basemap: vectorMap,
            ground: heightMap,
        });

        for (const configLayer of AppConfig.Layers) {
            let layer: FeatureLayerNFS | FeatureLayer | null = null;
            if (configLayer.Type === "FeatureLayer") {
                if (configLayer.IsVersioned) {
                    layer = new FeatureLayer({
                        // This is a critical "hack" to make sure postion of features are correct
                        portalItem: {
                            id: AppConfig.NFSMapFeaturesPortalItemId,
                            portal: {
                                url: AppConfig.ArcGIS.BaseUrl,
                            },
                        },
                        returnZ: true,
                        url: AppConfig.NauticalObjectsFeatureServer + configLayer.LayerId,
                        layerId: configLayer.LayerId,
                        id: configLayer.Name,
                        title: configLayer.Name,
                        visible: configLayer.DefaultVisible,
                        popupEnabled: false,
                        outFields: ["*"],
                    });

                    if (layer.layerId === FeatureClasses.FlytendemerkeTildelt) {
                        layer.minScale = 1000;
                    }
                } else {
                    layer = new FeatureLayer({
                        url: AppConfig.NauticalObjectsFeatureServer + configLayer.LayerId,
                        layerId: configLayer.LayerId,
                        returnZ: true,
                        id: configLayer.Name,
                        title: configLayer.Name,
                        visible: configLayer.DefaultVisible,
                        popupEnabled: false,
                        outFields: ["*"],
                    });
                }
            }

            if (layer) {
                (layer as FeatureLayerNFS).isVersioned = configLayer.IsVersioned;
                if (configLayer.IsTable) {
                    map.tables.add(layer);
                } else {
                    map.add(layer, configLayer.Order);
                    layer.when(
                        () => {
                            if (layerHasField(layer, "nfs_status")) {
                                layer.definitionExpression = removedAndDiscontinuedDefExpr(layer, showRemovedFeatures);
                            }
                            if (configLayer.Name === "IB") {
                                // As requested by Kystverket we hide IB that are linked to HIB
                                layer.definitionExpression += " AND tilknyttet_hib = 0";
                            }
                            if (configLayer.Name === "Flytende merke") {
                                // As requested by Kystverket we hide Flytende merke that are linked to lysbøye
                                layer.definitionExpression += " AND tilknyttet_lysboye = 0";
                            }
                            dispatch(addDefaultMinScale({ layerName: layer.id, minScale: layer.minScale }));
                        },
                        () => {
                            console.error("Error loading layer ", layer.id);
                        }
                    );
                }
            }
        }

        const externalSignsLayer = new FeatureLayer({
            url: AppConfig.NFSMapFeatureServer + "4",
            id: "Eksterne skilt",
            title: "Eksterne skilt",
            visible: false,
            popupEnabled: true,
            outFields: ["*"],
        });
        (externalSignsLayer as FeatureLayerNFS).includeInNFSLayers = true;
        map.add(externalSignsLayer);

        const merkeplanAreaLayer = new FeatureLayer({
            url: AppConfig.MerkeplanAreaLayerUrl,
            id: "Merkeplan område",
            title: "Merkeplan område",
            visible: true,
            popupEnabled: true,
            outFields: ["*"],
        });
        (merkeplanAreaLayer as FeatureLayerNFS).includeInNFSLayers = true;
        map.add(merkeplanAreaLayer);

        if (AppConfig.KyvDepthRasterLayerUrl !== "") {
            const kyvDepthRasterLayer = new ImageryLayer({
                url: AppConfig.KyvDepthRasterLayerUrl,
                id: "KYV Dybdedata Raster",
                title: "KYV Dybdedata Raster",
                visible: false,
            });
            (kyvDepthRasterLayer as ImageryLayerNFS).includeInNFSLayers = true;
            map.add(kyvDepthRasterLayer, 0);
        }

        addJobsTablesToMap(map);

        addAuxiliaryLayer(map, "peilelinjal");
        addAuxiliaryLayer(map, "angleLayer");
        addAuxiliaryLayer(map, "OverettlinjeEditing");
        addAuxiliaryLayer(map, "boatCoordinates");
        addAuxiliaryLayer(map, "FyrlyktEditing");
        addAuxiliaryLayer(map, "FyrlyktEditing2");
        addAuxiliaryLayer(map, "lighthouseOverlayLayer");
        addAuxiliaryLayer(map, "overettOverlayLayer");
        addAuxiliaryLayer(map, "seacableEditorLayer");

        setWebMap(map);

        return () => {
            map?.destroy();
            esriHandleRef.current.forEach((handle) => handle.remove());
            esriHandleRef.current = [];
        };
    }, []);

    return (
        <>
            {webMap && (
                <arcgis-map
                    id="map"
                    onarcgisViewReadyChange={handleViewReady}
                    constraints={{ snapToZoom: false, rotationEnabled: false } as View2DConstraints}
                    map={webMap}
                >
                    <div slot="bottom-right" className="flex-row small-gap" style={{ alignItems: "flex-end" }}>
                        <arcgis-scale-bar></arcgis-scale-bar>
                        <arcgis-locate></arcgis-locate>
                        <arcgis-zoom></arcgis-zoom>
                    </div>

                    <arcgis-measurement slot="bottom-right"></arcgis-measurement>

                    <div slot="bottom-left">
                        <Scale></Scale>
                    </div>
                    <arcgis-expand slot="bottom-left">
                        <arcgis-legend></arcgis-legend>
                    </arcgis-expand>
                    <arcgis-expand slot="bottom-left">
                        <arcgis-coordinate-conversion
                            expanded
                            className="nfs-coordinate-conversion"
                        ></arcgis-coordinate-conversion>
                    </arcgis-expand>
                    <MapOverlay />
                    {/* <arcgis-expand position="bottom-left">
                        <arcgis-arcade-editor position="bottom-left"></arcgis-arcade-editor>
                    </arcgis-expand> */}
                </arcgis-map>
            )}
            {/* Portalled to the body: .map-background is a stacking context (z-index 1), which kept
                the overlay behind the floating windows whatever its own z-index */}
            {loading && createPortal(<Loader overlay={"fullscreen"} />, document.body)}
        </>
    );
};

export default MapControl;
